export interface BudgetAssumptions {
  budget: number;
  cpc: number;
  conversionRate: number;
  averageOrderValue: number;
  organicOrderShare: number;
  nonAdCostRate: number;
}

export interface BudgetForecast {
  budget: number;
  totalOrders: number;
  totalSales: number;
  totalProfit: number;
  totalSpend: number;
  tacos: number | null;
  adSpend: number;
  adSales: number;
}

export const BUDGET_PERCENT_MIN = 50;
export const BUDGET_PERCENT_MAX = 200;
const CURVE_STEP_PERCENT = 2;

// Marginal response falls as budget rises and reaches zero near 142% of the chosen baseline.
// This is an illustrative planning assumption, not a fit to a shop's historical performance.
const BASELINE_RESPONSE_SLOPE = 0.5;
const RESPONSE_DECAY = 0.6;

function orderResponse(budgetRatio: number): number {
  const change = Math.min(budgetRatio - 1, BASELINE_RESPONSE_SLOPE / (2 * RESPONSE_DECAY));
  return Math.max(0, 1 + BASELINE_RESPONSE_SLOPE * change - RESPONSE_DECAY * change * change);
}

/** Keep the documented 100% example exact, then apply diminishing marginal orders around it. */
export function forecastBudget(
  assumptions: BudgetAssumptions,
  budget = assumptions.budget,
): BudgetForecast {
  const baselineAdOrders =
    (assumptions.budget / assumptions.cpc) * (assumptions.conversionRate / 100);
  const adOrders = baselineAdOrders * orderResponse(budget / assumptions.budget);
  const totalOrders = adOrders / (1 - assumptions.organicOrderShare / 100);
  const totalSales = totalOrders * assumptions.averageOrderValue;
  const adSales = adOrders * assumptions.averageOrderValue;
  const totalSpend = totalSales * (assumptions.nonAdCostRate / 100) + budget;

  return {
    budget,
    totalOrders,
    totalSales,
    totalProfit: totalSales - totalSpend,
    totalSpend,
    tacos: totalSales > 0 ? (budget / totalSales) * 100 : null,
    adSpend: budget,
    adSales,
  };
}

export function budgetCurve(
  assumptions: BudgetAssumptions,
): Array<BudgetForecast & { percent: number }> {
  return Array.from(
    { length: (BUDGET_PERCENT_MAX - BUDGET_PERCENT_MIN) / CURVE_STEP_PERCENT + 1 },
    (_, index) => {
      const percent = BUDGET_PERCENT_MIN + index * CURVE_STEP_PERCENT;
      return {
        percent,
        ...forecastBudget(assumptions, (assumptions.budget * percent) / 100),
      };
    },
  );
}
